<template>
  <pui-flex flex="1" class="pui-folder-tree-root">
    <pui-common-spinner v-if="loadingRoots" position="fixed" />

    <pui-tree
      ref="tree"
      :roots="filteredRoots"
      :options="treeOptions"
      v-on="computedListeners"
      v-bind="treeProps"
      class="pui-folder-tree"
    >
      <template #checkbox="nodeSlotProps">
        <pui-folder-node-checkbox
          v-if="canDisplayCheckboxForNode(nodeSlotProps.node)"
          v-bind="nodeSlotProps"
          :state="getSelectionState(nodeSlotProps.node)"
          :hasChildren="nodeSlotProps.node.hasChildren"
          @click.stop="onClickNodeHook(nodeSlotProps.node, false, $event)"
        />
      </template>

      <template #name="nodeSlotProps">
        <template v-if="nodeSlotProps.node._is_error_node">
          <pui-flex class="pui-folder-tree-error-node-name" direction="column">
            {{ $t('dam.folders.tree.error.message') }}
            <a @click="addChildrenFolders(nodeSlotProps.parentNode)" class="node-reload pui-link">{{
              $t('dam.folders.tree.error.reload')
            }}</a>
          </pui-flex>
        </template>
        <template v-else>
          <slot v-bind="nodeSlotProps" name="nodeNameLabel">
            {{ piivoTranslate(nodeSlotProps.node) }}
          </slot>
        </template>
      </template>

      <template #afterName="nodeSlotProps">
        <slot v-bind="nodeSlotProps" name="afterName" />
        <div v-if="nodeSlotProps.node._isLoading">
          <pui-common-spinner
            ref="masonrySpinner"
            :size="20"
            :strokeWidth="2"
            :hideMessage="true"
            class="folder-spinner"
          />
        </div>
        <slot v-bind="nodeSlotProps" name="afterNameAfterMeta" />
      </template>
    </pui-tree>
  </pui-flex>
</template>

<script>
import { TREE_ROOT_ID } from 'piivo-ui/src/components/tree/treeConstants';
import Vue from 'vue';

import {
  CHECKED_ALL_CHILDREN_STATES,
  CHECKED_SOME_CHILDREN_STATES,
  CHECKED_STATES,
  SelectionState,
} from '../../constants/folderTree';
import { getFolderIdsFromPath } from '../../utils/folders';
import puiFolderNodeCheckbox from './FolderNodeCheckbox';
import { getDamService } from '../../services';

/**
 * @typedef FolderSelection
 * @type {object}
 * @property {object} node - the folder node
 * @property {string} state - the selection state of the node
 */

const getNodeErrorId = nodeId => `${nodeId}_error`;

const CLICK_NODE_TIMEOUT = 500;

const generateErrorNode = node => ({
  itemId: getNodeErrorId(node._tree_node_id),
  _is_error_node: true,
  options: {
    hideCheckbox: true,
    noSelection: true,
    contentWrapperClass: {
      'pui-folder-tree-error-node-wrapper': true,
    },
  },
});

export default {
  name: 'pui-folder-tree',
  components: {
    puiFolderNodeCheckbox,
  },
  props: {
    /**
     * Options for the tree
     *
     * @type {Object}
     * @default {}
     */
    options: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Retrieves the roots
     */
    getRoots: {
      type: Function,
      required: true,
    },
    /**
     * The paths of which folders should
     * be displayed. All selections will be made
     * against the filtered folders
     */
    whitelistedPaths: {
      type: Array,
      default: () => [],
    },
    /**
     * Extra props for the basic tree component
     */
    treeProps: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Optional sort object. Will be passed to the `getRoots` callback
     * and used (internally) when loading children.
     * The roots will automatically be reloaded when this changes
     */
    sort: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loadingRoots: true,
      rootFolders: [],
      nodeSelectionState: {},
      hasFirstClick: false,
    };
  },
  computed: {
    /**
     * @returns {object} options for the Tree component
     */
    computedTreeOptions() {
      return {
        keyboardNavigation: true,
        enableUpperRecursiveSelection: false,
        ...this.treeOptions,
      };
    },
    /**
     * @returns {object} all the listeners for the Tree componenet
     */
    computedListeners() {
      return {
        ...this.$listeners,
        toggleExpandedNode: this.onNodeExpansionToggled,
        // Intermediate method to properly pass params
        clickNode: this.onTreeNodeClicked,
      };
    },
    /**
     * @returns {object} object map of all the folders
     * in the tree (raw tree, not the filtered objects)
     */
    foldersMap() {
      const allFolders = {};

      const mapNode = node => ({ ...node, children: [...node._allChildren] });

      const foldersToProcess = [...this.rootFolders].map(node => mapNode(node));

      let singleFolder = foldersToProcess.shift();

      while (singleFolder) {
        this.$set(allFolders, singleFolder.itemId, mapNode(singleFolder));

        if (singleFolder.hasChildren && singleFolder._allChildren.length) {
          foldersToProcess.push(...singleFolder._allChildren);
        }

        singleFolder = foldersToProcess.shift();
      }

      return allFolders;
    },
    /**
     * WARNING: if you manually set the selection state, you might
     * have 'checked' folder states for folders that are not yet in the
     * tree. If so, they will not be present in this array
     *
     * @returns {FolderSelection[]} array of all the selected folders in the tree
     * (where state is not NONE)
     */
    selection() {
      const selection = [];

      for (const nodeId in this.foldersMap) {
        const node = this.foldersMap[nodeId];
        const nodeSelectionState = this.getSelectionState(node);

        if (nodeSelectionState !== SelectionState.None) {
          selection.push({
            node,
            state: nodeSelectionState,
          });
        }
      }

      return selection;
    },
    /**
     * @returns {object[]} root folders that are in the whitelist, with
     * the children recursively filtered
     */
    filteredRoots() {
      // Always call the filter function so that
      // deep changes in rootFolders will be tracked
      const filteredRoots = this.filterFolderArray(this.rootFolders);

      return filteredRoots;
    },
    /**
     * @returns {object} options for the Tree component
     */
    treeOptions() {
      // Duplicate the options prop
      const copiedOptions = { ...this.options };

      // Delete options for our component
      delete copiedOptions.checkbox;

      return copiedOptions;
    },
  },
  watch: {
    // Reload roots when sort changes
    sort: 'getRootFolders',
  },
  methods: {
    // FILTERS
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * @param {object} node - a node in the tree
     * @returns {boolean} if should display a checkbox for the node
     */
    canDisplayCheckboxForNode(node) {
      return (
        this.options.checkbox &&
        (!Object.hasOwnProperty.call(node.options, 'hideCheckbox') || !node.options.hideCheckbox)
      );
    },
    /**
     * @param {object} node - a node in the tree
     * @returns {boolean} if the node can be selected
     */
    canSelectNode(node) {
      return !Object.hasOwnProperty.call(node.options, 'noSelection') || !node.options.noSelection;
    },
    // TREE
    /**
     * Filters an array of folders according to the whitelist
     *
     * @param {object[]} sourceArr - the array of folders to filter
     * @returns {object[]} array of folders in the whitelist, with their
     * children also filtered
     */
    filterFolderArray(sourceArr) {
      const filteredFolders = [];

      sourceArr.forEach(folder => {
        // If there is no whitelist, simply add
        // the folder with all children
        if (!this.whitelistedPaths.length) {
          // We still need to recurse so we can map '_allChildren' to 'chilren'
          // to all recursive children
          const children = this.filterFolderArray(folder._allChildren);

          const filteredFolder = {
            ...folder,
            children,
          };

          filteredFolders.push(filteredFolder);
          return;
        }

        // Else compute whether it is in the whitelist

        let isInWhitelistedPath = false;
        let hasWhitelistedChildren = false;

        const paths = [...this.whitelistedPaths];
        let path = paths.shift();

        // Iterate over all the whitelisted paths until we can tell
        // whether we need to recurse or not
        while ((!isInWhitelistedPath || !hasWhitelistedChildren) && path) {
          const pathFolderIds = getFolderIdsFromPath(path);
          const indexOfId = pathFolderIds.indexOf(folder.itemId);
          if (indexOfId >= 0) {
            isInWhitelistedPath = true;

            const wasLastInPath = indexOfId === pathFolderIds.length - 1;
            if (isInWhitelistedPath && !wasLastInPath) {
              hasWhitelistedChildren = true;
            }
          }

          path = paths.shift();
        }

        // If this folder is not in any whitelisted path,
        // ignore it
        if (!isInWhitelistedPath) {
          return;
        }

        // If there are whitelisted children, filter them.
        // Else simply use an empty array to prevent useless recursion
        const filteredChildren = hasWhitelistedChildren
          ? this.filterFolderArray(folder._allChildren)
          : [];

        const filteredFolder = {
          ...folder,
          children: filteredChildren,
          options: {
            ...folder.options,
            hasChildren: hasWhitelistedChildren,
          },
        };

        filteredFolders.push(filteredFolder);
      });

      return filteredFolders;
    },
    /**
     * Function to get root folders in mounted component hook
     */
    async getRootFolders() {
      this.loadingRoots = true;

      try {
        const apiRoots = await this.getRoots(this.sort);
        const rootFolders = apiRoots.map(node => {
          return this.mapNode(node);
        });

        this.rootFolders = rootFolders;

        this.$emit('loadedFolders', rootFolders, this.getTreeRoots());

        const hasOpenRoots = this.rootFolders.some(
          root => !!this.$refs.tree.getExpansionState()[root.itemId]
        );
        if (this.options.autoExpandRoots || hasOpenRoots) {
          // Wait for roots to be passed to tree
          await this.$nextTick();
          for (const root of this.rootFolders) {
            if (this.$refs.tree.getExpansionState()[root.itemId]) {
              // If the node is already expanded, retrigger the expansion handler
              // to load the children
              await this.onNodeExpansionToggled(root, true);
            } else if (this.options.autoExpandRoots) {
              this.expandFolder(root.itemId, null);
            }
          }
        }
      } catch (error) {
        this.$emit('error', 'root', error);
      }

      this.loadingRoots = false;
    },
    /**
     * Function to generate technical property on node
     * @param {Object} node to add technical properties
     * @returns {Object} node - node with technical properties
     */
    mapNode(node) {
      // Technical id for tree
      this.$set(node, '_tree_node_id', node.itemId);
      // Property if node is currently loading children
      this.$set(node, '_isLoading', false);
      // Property if node children call is in error
      this.$set(node, '_hasError', false);

      this.$set(node, 'options', node.options || {});
      this.$set(node, 'children', node.children || []);
      this.$set(node, '_allChildren', node._allChildren || []);
      // Property to display folder icons if have children
      this.$set(node.options, 'hasChildren', node.hasChildren);
      this.$set(node.options, 'contentWrapperClass', {
        [node.options.contentWrapperClass]:
          typeof node.options.contentWrapperClass === 'string' && !!node.options.contentWrapperClass
            ? node.options.contentWrapperClass
            : '',
        ...(typeof node.options.contentWrapperClass === 'object' &&
        !!node.options.contentWrapperClass
          ? node.options.contentWrapperClass
          : {}),
        'folder-tree__node': true,
        checked: false,
      });
      // Restore selection state (if the node didn't have any state, it won't make a difference)
      this.internalSetNodeIdSelectionState(node.itemId, this.getSelectionState(node));
      return node;
    },
    /**
     * Loads children of the node from the api
     * into the node's children array
     *
     * @param {Object} node - node to add children to (potentially the filtered object, not the raw object)
     * @param {boolean} mergeChildren - if the newly retrieved children
     * should be merged with old children (to preserve state of the old children)
     * @returns {Promise}
     */
    async addChildrenFolders(node, mergeChildren) {
      node._hasError = false;
      node._isLoading = true;

      // Remove the error node while loading
      const errorNodeIndex = node._allChildren.findIndex(
        childNode => childNode._tree_node_id === getNodeErrorId(node._tree_node_id)
      );
      if (errorNodeIndex >= 0) {
        node._allChildren.splice(errorNodeIndex, 1);
      }

      try {
        const children = await getDamService('foldersService')
          .getChildrenFolders('foldersTree', node.itemId, this.sort)
          .then(res => res.body.items);

        if (!children.length) {
          throw new Error(
            `Attempted to load children of ${this.piivoTranslate(node)}:${
              node.itemId
            }, but received none`
          );
        }

        // Generate technical props on each subFolder
        const nextChildren = mergeChildren
          ? this.mergeFoldersLevel(children, node._allChildren)
          : children.map(item => this.mapNode(item));

        // Replace all previous children with the newly mapped children.
        // Use splice instead of assignment: the array reference is the same across
        // the filtered node object and the raw node object
        node._allChildren.splice(0, node._allChildren.length, ...nextChildren);
        this.$emit('loadedFolders', nextChildren, this.getTreeRoots());
        // Mark the children as checked if parent is already checked
        if (CHECKED_ALL_CHILDREN_STATES.includes(this.getSelectionState(node))) {
          nextChildren.forEach(child => {
            // For every node, mark 'all children' only
            // if it actually has children
            const state = child.hasChildren
              ? SelectionState.MeAndAllChildren
              : SelectionState.MeOnly;

            this.internalSetNodeIdSelectionState(child.itemId, state);
          });
        }

        // Since we replaced the old children with new objects,
        // they won't have any grandchildren. So we have to reset
        // their expansion.
        this.$nextTick(() => {
          nextChildren.forEach(child => {
            this.$refs.tree.setNodeExpansionState(child, false);
          });
        });
      } catch (error) {
        node._hasError = true;
        const errorNode = this.mapNode(generateErrorNode(node));

        node._allChildren.splice(0, node._allChildren.length, errorNode);
        this.$emit('error', 'children', error);
      }

      node._isLoading = false;
    },
    /**
     * Function calls when node expansion toggled
     * @param {Object} node expansion toggled
     * @param {Boolean} nodeExpansionState - value of expansion
     * @param {Object} expansionState - key value with each expansion node state
     * @returns {void}
     */
    async onNodeExpansionToggled(node, nodeExpansionState, expansionState) {
      // Load children when we expand the node and we **didn't** already load children
      if (nodeExpansionState && node.hasChildren && !node._allChildren.length) {
        await this.addChildrenFolders(node, false);
      }
    },
    /**
     * @returns {string} the selection state of the node
     */
    getSelectionState(node) {
      return this.nodeSelectionState[node.itemId] || SelectionState.None;
    },
    /**
     * Handles a click on the node, from the tree component
     * @param {Object} node - node clicked (the filtered node object, not the actual node)
     * @param {Object} nodeSlotProps - props for what a slot would
     * receive to render the node
     * @param {MouseEvent} event - the click event
     */
    onTreeNodeClicked(node, nodeSlotProps, event) {
      this.onClickNodeHook(node, true, event);
    },
    /**
     * Updates the node selection state when a node is clicked
     *
     * @param {Object} node - node clicked (the filtered node object, not the actual node)
     * @param {boolean} clearOtherNodeStates - if this should clear checked state
     * of nodes whose path does not contain the clicked node. Only used when `options.checkbox`
     * @param {MouseEvent} event - the click event
     */
    onClickNodeHook(node, clearOtherNodeStates, event) {
      clearTimeout(this.onClickNodeTimeout);

      if (!this.hasFirstClick) {
        this.onClickNode(node, clearOtherNodeStates, event);

        this.hasFirstClick = true;
        this.onClickNodeTimeout = setTimeout(() => {
          this.hasFirstClick = false;
        }, CLICK_NODE_TIMEOUT);
      } else {
        // Clear the bool immediately, so a 3rd click will
        // be handled as a normal click, instead of another double-click
        this.hasFirstClick = false;

        const prevSelection = [...this.selection];

        if (!node.hasChildren) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeOnly);
        } else {
          this.recurseDown(node, folder => {
            // For every node, mark 'all children' only
            // if it actually has children
            const state = folder.hasChildren
              ? SelectionState.MeAndAllChildren
              : SelectionState.MeOnly;

            this.internalSetNodeIdSelectionState(folder.itemId, state);
          });
        }

        // Always recurse upwards
        this.updateParentForChildrenState(node);

        this.$emit('selectionChange', [...this.selection], prevSelection);

        this.$emit('selectionStateChange', { ...this.nodeSelectionState });
      }
    },
    /**
     * Updates the node selection state when a node is clicked
     *
     * @param {Object} node - node clicked (the filtered node object, not the actual node)
     * @param {boolean} clearOtherNodeStates - if this should clear checked state
     * of nodes whose path does not contain the clicked node. Only used when `options.checkbox`
     * @param {MouseEvent} event - the click event
     */
    onClickNode(node, clearOtherNodeStates, event) {
      if (!this.canSelectNode(node)) {
        return;
      }

      // If we should cycle through the check states
      let doCheckCycle = true;

      const prevSelection = [...this.selection];
      let state = this.getSelectionState(node);

      if (!this.options.checkbox) {
        // Clear all old selection
        Object.keys(this.nodeSelectionState).forEach(oldNodeId => {
          this.internalSetNodeIdSelectionState(oldNodeId, SelectionState.None);
        });
        // Select the clicked node
        this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeOnly);
        state = this.getSelectionState(node); // Update the new state for recursive calculations
        // But in this case, we only want the current node to be selected,
        // thus we do not need to recurse down
        doCheckCycle = false;
      } else if (clearOtherNodeStates) {
        let hasOtherSelection = false;
        // Clear all old selection
        Object.keys(this.nodeSelectionState).forEach(oldNodeId => {
          if (this.nodeSelectionState[oldNodeId] === SelectionState.None) {
            // If an old node has NONE selection state, we can ignore it
            return;
          }

          // If another node besides the clicked one has a selection state, clear it and set the flag
          if (oldNodeId !== node.itemId) {
            hasOtherSelection =
              hasOtherSelection || CHECKED_STATES.includes(this.nodeSelectionState[oldNodeId]);

            this.internalSetNodeIdSelectionState(oldNodeId, SelectionState.None);
          }
        });

        // If any other nodes were in any selection state, we want to select ONLY this one
        // and NOT do the cycle
        if (hasOtherSelection) {
          // Select the clicked node
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeOnly);
          state = this.getSelectionState(node); // Update the new state for recursive calculations
          // But in this case, we only want the current node to be selected,
          // thus we do not need to recurse down
          doCheckCycle = false;
        }
      }

      if (doCheckCycle) {
        switch (state) {
          case SelectionState.None: {
            this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeOnly);
            break;
          }
          case SelectionState.MeOnly: {
            if (node.hasChildren) {
              this.recurseDown(node, folder => {
                // For every node, mark 'all children' only
                // if it actually has children
                const state = folder.hasChildren
                  ? SelectionState.MeAndAllChildren
                  : SelectionState.MeOnly;

                this.internalSetNodeIdSelectionState(folder.itemId, state);
              });
            } else {
              this.internalSetNodeIdSelectionState(node.itemId, SelectionState.None);
            }
            break;
          }
          case SelectionState.MeAndSomeChildren: {
            this.recurseDown(node, folder => {
              // For every node, mark 'all children' only
              // if it actually has children
              const state = folder.hasChildren
                ? SelectionState.MeAndAllChildren
                : SelectionState.MeOnly;

              this.internalSetNodeIdSelectionState(folder.itemId, state);
            });
            break;
          }
          case SelectionState.NotMeButSomeChildren: {
            this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeAndSomeChildren);
            break;
          }
          case SelectionState.NotMeButAllChildren: {
            this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeAndAllChildren);
            break;
          }
          case SelectionState.MeAndAllChildren: {
            this.recurseDown(node, folder => {
              this.internalSetNodeIdSelectionState(folder.itemId, SelectionState.None);
            });
            break;
          }
          default:
            break;
        }
      }

      // Always recurse upwards
      this.updateParentForChildrenState(node);

      this.$emit('selectionChange', [...this.selection], prevSelection);

      this.$emit('selectionStateChange', { ...this.nodeSelectionState });
    },
    /**
     * Updates the node's state according to its current state and
     * the state of its children
     *
     * @param {object} node - the node whose state to update
     * @returns {void}
     */
    updateParentForChildrenState(node) {
      const parent = this.findParent(node.itemId);
      if (parent && parent.itemId !== TREE_ROOT_ID) {
        const isNodeChecked = CHECKED_STATES.includes(this.getSelectionState(parent));
        this.updateNodeForChildrenState(parent, isNodeChecked);
      }
    },
    /**
     * Updates the node's state according to whether it's checked
     * and the state of its children
     *
     * @param {object} node - the node whose state to update
     * @param {boolean} isNodeChecked - if the node is currently considered
     * as checked
     * @returns {void}
     */
    updateNodeForChildrenState(node, isNodeChecked) {
      let nCheckedChildren = 0;

      for (const child of node.children) {
        if (CHECKED_STATES.includes(this.getSelectionState(child))) {
          nCheckedChildren++;
        }
      }

      /**
       * If ALL the children (incl. recursive) are checked
       */
      let checkedAllRecursive = false;
      /**
       * If SOME of the children (incl. recursive) are checked
       */
      let checkedSomeRecursive = false;

      // We can directly check if every child is 'ME_AND_ALL_CHILDREN' or 'ME_ONLY'
      // instead of recursing down
      node.children.forEach((child, childIndex) => {
        const folderState = this.nodeSelectionState[child.itemId];

        /**
         * If this child is checked
         */
        const isChecked = CHECKED_STATES.includes(folderState);
        /**
         * If this child recursively contains some checked children
         */
        const someRecursiveChildrenAreChecked = CHECKED_SOME_CHILDREN_STATES.includes(folderState);

        const isChildRecursivelyChecked = child.hasChildren
          ? SelectionState.MeAndAllChildren === folderState
          : isChecked;

        // If this is the first child, the 'all' state is just this child's
        // Else, it is the previous state AND this child's state
        checkedAllRecursive =
          isChildRecursivelyChecked && (childIndex === 0 || checkedAllRecursive);

        // Our outside node has some recursive checked children, either if
        // - a direct child is checked
        // - a direct child has some recursive children checked
        checkedSomeRecursive = isChecked || someRecursiveChildrenAreChecked || checkedSomeRecursive;
      });

      if (isNodeChecked) {
        if (checkedAllRecursive) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeAndAllChildren);
        } else if (checkedSomeRecursive) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeAndSomeChildren);
        } else if (nCheckedChildren === 0) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.MeOnly);
        }
      } else {
        if (checkedAllRecursive) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.NotMeButAllChildren);
        } else if (checkedSomeRecursive) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.NotMeButSomeChildren);
        } else if (nCheckedChildren === 0) {
          this.internalSetNodeIdSelectionState(node.itemId, SelectionState.None);
        }
      }

      this.updateParentForChildrenState(node);
    },
    /**
     * Recurses down the folder tree to apply the callback
     * on the folders
     *
     * @param {object} folder - the initial folder
     * @param {(folder: object) => void} callback - the callback to
     * apply on the folders
     */
    recurseDown(folder, callback) {
      const allFolders = [];

      let singleFolder = folder;

      while (singleFolder) {
        callback(singleFolder);

        if (singleFolder.hasChildren && singleFolder.children.length) {
          allFolders.push(...singleFolder.children);
        }

        singleFolder = allFolders.shift();
      }
    },
    /**
     * @param {string} folderId - the id of the folder whose
     * parent to find
     * @returns {object} the parent of the folder
     */
    findParent(folderId) {
      for (const nodeId in this.foldersMap) {
        const node = this.foldersMap[nodeId];
        if (node.hasChildren && !!node.children.find(child => child.itemId === folderId)) {
          return node;
        }
      }

      return null;
    },
    /**
     * Will set the node selection state
     *
     * @param {string} nodeId - the id of the node whose state to set
     * @param {string} state - the selection state to set
     */
    internalSetNodeIdSelectionState(nodeId, state) {
      this.$set(this.nodeSelectionState, nodeId, state);
      const node = this.foldersMap[nodeId];
      if (node) {
        const isChecked = CHECKED_STATES.includes(state);
        node.options.contentWrapperClass.checked = isChecked;
      }
    },
    /**
     * Merges array of folders, using the api level as source of truth.
     * New folders are mapped, old folders are not touched, deleted folders
     * are simply removed.
     *
     * @param {object[]} nextApiLevel - the raw array of folders from the api
     * @param {object[]} oldLevel - the old folder objects
     */
    mergeFoldersLevel(nextApiLevel, oldLevel) {
      return nextApiLevel.map(nextFolder => {
        const oldFolder = oldLevel.find(o => o.itemId === nextFolder.itemId);
        if (oldFolder) {
          return oldFolder;
        }
        return this.mapNode(nextFolder);
      });
    },
    // EXPOSED METHODS
    /**
     * @returns {object[]} the tree roots
     */
    getTreeRoots() {
      return [...this.filteredRoots];
    },
    /**
     * Will set the node selection state
     *
     * @param {string} nodeId - the id of the node whose state to set
     * @param {string} state - the selection state to set
     * @param {boolean} recurse - if the selection should update children
     * and parents
     */
    setNodeIdSelectionState(nodeId, state, recurse) {
      this.internalSetNodeIdSelectionState(nodeId, state);

      if (recurse) {
        const node = this.foldersMap[nodeId];
        if (node && recurse) {
          // This logic is different from the one in 'clickNode', because
          // clickNode looks at the current state to apply a new one.
          // Whereas here, we know the new state. We just need to apply to to children
          switch (state) {
            case SelectionState.None: {
              // No recursion necessary
              break;
            }
            case SelectionState.MeOnly: {
              if (node.hasChildren) {
                this.recurseDown(node, folder => {
                  this.internalSetNodeIdSelectionState(folder.itemId, SelectionState.None);
                });
              }
              break;
            }
            case SelectionState.MeAndSomeChildren: {
              // We do not know which children are selected
              // so do nothing
              break;
            }
            case SelectionState.NotMeButSomeChildren: {
              // We do not know which children are selected
              // so do nothing
              break;
            }
            case SelectionState.NotMeButAllChildren: {
              this.recurseDown(node, folder => {
                if (folder.itemId !== node.itemId) {
                  // For every node, mark 'all children' only
                  // if it actually has children
                  const state = folder.hasChildren
                    ? SelectionState.MeAndAllChildren
                    : SelectionState.MeOnly;

                  this.internalSetNodeIdSelectionState(folder.itemId, state);
                }
              });
              break;
            }
            case SelectionState.MeAndAllChildren: {
              this.recurseDown(node, folder => {
                // For every node, mark 'all children' only
                // if it actually has children
                const state = folder.hasChildren
                  ? SelectionState.MeAndAllChildren
                  : SelectionState.MeOnly;

                this.internalSetNodeIdSelectionState(folder.itemId, state);
              });
              break;
            }
            default:
              break;
          }

          // Always recurse upwards
          this.updateParentForChildrenState(node);
        }
      }
    },
    /**
     * Clears the node selection state
     *
     * @returns {void}
     */
    clearNodeSelectionState() {
      this.nodeSelectionState = {};
    },
    /**
     * Expands the folder if it is in the tree and
     * loads its children. Will not do anything
     * if the folder is already expanded
     *
     * @param {string} folderId - the id of the folder
     * to expand
     * @param {string[]} [parentFolderPath=null] - path to the folder, folder included
     * if given, will scroll to the node
     * @returns {Promise}
     */
    async expandFolder(folderId, parentFolderPath = null) {
      const folderNode = this.foldersMap[folderId];
      if (!folderNode) {
        return;
      }

      if (parentFolderPath) {
        this.$refs.tree.scrollToPath([...parentFolderPath], { behavior: 'smooth' });
      }

      if (!this.$refs.tree.getExpansionState()[folderId]) {
        this.$refs.tree.setNodeExpansionState(folderNode, true);
        await this.onNodeExpansionToggled(folderNode, true, this.$refs.tree.getExpansionState());
      }
    },
    /**
     * @returns {object[]} a shallow copy of the current selection
     */
    getFolderSelection() {
      return [...this.selection];
    },
    /**
     * @returns {object[]} a copy of the folder selection state
     */
    getFolderSelectionState() {
      return { ...this.nodeSelectionState };
    },
    /**
     * Refreshes the root folders by merging with the old state
     */
    async refreshRoots() {
      this.loadingRoots = true;

      try {
        const apiRoots = await this.getRoots(this.sort);
        const nextRoots = this.mergeFoldersLevel(apiRoots, this.rootFolders);
        this.rootFolders = nextRoots;
        this.$emit('loadedFolders', nextRoots, this.getTreeRoots());

        if (this.options.autoExpandRoots) {
          // Wait for roots to be passed to tree
          await this.$nextTick();
          for (const root of this.rootFolders) {
            // ExpandFolder only expands non-expanded folders
            // so we can safely call it on all roots
            await this.expandFolder(root.itemId, null);
          }
        }
      } catch (error) {
        this.$emit('error', 'root', error);
        return false;
      }

      this.loadingRoots = false;
      return true;
    },
    /**
     * Refreshes a single folder. If it was expanded,
     * the children will be reloaded and merged with any
     * existing children.
     *
     * @param {string} folderId - id of the folder to refresh
     * @returns {boolean} if the refresh was successful
     */
    async refreshFolder(folderId) {
      const oldFolder = this.foldersMap[folderId];
      if (!oldFolder) {
        return false;
      }
      oldFolder._hasError = false;
      oldFolder._isLoading = true;

      // Remove the error node while loading
      const errorNodeIndex = oldFolder._allChildren.findIndex(
        childNode => childNode._tree_node_id === getNodeErrorId(oldFolder._tree_node_id)
      );
      if (errorNodeIndex >= 0) {
        oldFolder._allChildren.splice(errorNodeIndex, 1);
      }

      try {
        const apiFolder = await getDamService('foldersService')
          .getFolder('foldersTree', oldFolder.itemId)
          .then(res => res.body);

        const loadedFolder = this.mapNode(apiFolder);
        this.$emit('loadedFolders', [loadedFolder], this.getTreeRoots());
        // Delete the children from the newly mapped node so we
        // can preserve any existing children when merging the object
        delete loadedFolder._allChildren;
        Object.assign(oldFolder, loadedFolder);
        // Reapply selection state
        this.internalSetNodeIdSelectionState(oldFolder.itemId, this.getSelectionState(oldFolder));

        if (this.$refs.tree.getExpansionState()[oldFolder.itemId]) {
          // If the folder was expanded, reload the children
          // but merge with previous rray
          await this.addChildrenFolders(oldFolder, true);
        } else {
          // If the folder was closed, remove all the children so they can be reloaded
          // on the next expansion
          oldFolder._allChildren.splice(0, oldFolder._allChildren.length);
        }
      } catch (err) {
        oldFolder._hasError = true;
        const errorNode = this.mapNode(generateErrorNode(oldFolder));

        oldFolder._allChildren.splice(0, oldFolder._allChildren.length, errorNode);
        this.$emit('error', 'children', error);

        oldFolder._isLoading = false;
        return false;
      }

      oldFolder._isLoading = false;
      return true;
    },
  },
  /**
   * Component mounted hook
   */
  mounted() {
    this.getRootFolders();
  },
};
</script>
